import { Grid } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader/PageHeader";
import CategoryTabs from "../../components/RestaurantMenuPage/CategoryTabs/CategoryTabs";
import MenuSections from "../../components/RestaurantMenuPage/MenuSections/MenuSections";
import OrderTypeTabs from "../../components/RestaurantMenuPage/OrderTypeTabs/OrderTypeTabs";
import AppNavbar from "../../components/shared/AppNavbar/AppNavbar";
import AddressSelectionModal from "../../components/shared/Modals/AddressSelectionModal/AddressSelectionModal";
import RestaurantErrorModal from "../../components/shared/Modals/RestaurantErrorModal/RestaurantErrorModal";
import ShiftInfo from "../../components/shared/ShiftInfo/ShiftInfo";
import { MODE_DELIVERY } from "../../constants/constants";
import { useConfig } from "../../context/ConfigContext";
import withNavigateHook from "../../hooks/withNavigateHook";
import { useMenuScroll } from "../../hooks/useMenuScroll";
import { languageData } from "../../i18n";
import { Category, Restaurant, RestaurantCart } from "../../interfaces/Catalogue";
import { shopApi } from "../../services/apiGate";
import { catalogueApi } from "../../services/catalogueApi";
import { deliveryApi } from "../../services/deliveryApi";
import { loyalityApi } from "../../services/loyalityApi";
import { clearStoreItem } from "../../store/actions/actions";
import { updateAppState } from "../../store/app/appActions";
import { getCategoriesRequest } from "../../store/categories/categoriesActions";
import { setDeliveryAddress } from "../../store/delivery/deliveryActions";
import { RootState } from "../../store/rootState";
import { filterCategoriesByTypeOrder } from "../../utils/menuUtils";
import { navigateBasedOnRestaurantCount } from "../../utils/navigationUtils";
import { getRestaurantDefaultMode } from "../../utils/orderUtils";
import { findCurrentShift } from "../../utils/shopSubtittleFormat";
import { useMemo } from "react";
import "./RestaurantMenuPage.scss";

const RestaurantMenuPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { restaurantId } = useParams();
  const location = useLocation();
  const { currentCategoryId } = location.state || {};
  const { selectedCategory } = location.state || {};

  const { config } = useConfig();
  const { establishementCount: restaurantCount = 0, isGlobalWebsite = false } = config || {};

  const userInfo = useSelector((s: any) => s.auth.userInfo);
  const authLocation = useSelector((s: any) => s.auth.location);
  const menuMode = useSelector((s: any) => s.menu.mode);
  const restaurantCarts = useSelector((s: any) => s.cart.restaurantCarts);
  const selectedMode = useSelector((s: RootState) => s.app.selectedMode);
  const { categories } = useSelector((s: any) => s.categories);

  const restaurantCart: RestaurantCart = restaurantCarts[Number(restaurantId)] || {};

  const filteredCategories = useMemo(
    () => filterCategoriesByTypeOrder(categories, selectedMode),
    [categories, selectedMode]
  );

  const [loadingState, setLoadingState] = useState(false);
  const [currentShifts, setCurrentShifts] = useState<serviceMenuModels.Shift>();
  const [RestorentFilter, setRestorentFilter] = useState("");
  const [restaurant, setRestaurant] = useState<Restaurant>();
  const [establishementToken, setEstablishementToken] = useState(
    localStorage.getItem("establishementToken")
  );
  const [userLoyalityPoints, setUserLoyalityPoints] = useState(0);
  const [showRestaurantError, setShowRestaurantError] = useState(false);
  const [showModal, setShowModal] = useState(
    selectedMode === MODE_DELIVERY && restaurantCart.mode !== MODE_DELIVERY
  );

  const hasInitialScrolled = useRef(false);

  const {
    activeTabIndex,
    setActiveTabIndex,
    sectionRefs,
    tabsContainerRef,
    scrollToSection,
    isScrollingProgrammatically,
  } = useMenuScroll(filteredCategories.length);

  // Scroll to category passed via navigation state
  useEffect(() => {
    if (filteredCategories.length > 0 && currentCategoryId) {
      const idx = filteredCategories.findIndex(
        (c: Category) => c.id === Number(currentCategoryId)
      );
      if (idx !== -1) scrollToSection(idx);
    }
  }, [currentCategoryId, filteredCategories, scrollToSection]);

  // One-time initial scroll to selectedCategory
  useEffect(() => {
    if (
      hasInitialScrolled.current ||
      !selectedCategory ||
      filteredCategories.length === 0 ||
      !restaurant ||
      loadingState ||
      sectionRefs.current.length === 0
    )
      return;

    const idx = filteredCategories.findIndex(
      (c: Category) =>
        c.id === selectedCategory.id ||
        c.designation === selectedCategory.designation ||
        c.designation.toLowerCase() === selectedCategory.designation?.toLowerCase()
    );
    if (idx === -1) return;

    hasInitialScrolled.current = true;
    setTimeout(() => {
      isScrollingProgrammatically.current = true;
      setActiveTabIndex(idx);
      const el = sectionRefs.current[idx];
      if (el) {
        window.scrollTo({ top: Math.max(0, el.offsetTop - 180), behavior: "smooth" });
        setTimeout(() => (isScrollingProgrammatically.current = false), 1000);
      }
    }, 400);
  }, [filteredCategories, selectedCategory, restaurant, loadingState]);

  useEffect(() => {
    hasInitialScrolled.current = false;
  }, [restaurantId]);

  // Fetch restaurant + token
  useEffect(() => {
    if (!restaurantId) return;
    const fetchRestaurant = async () => {
      setLoadingState(true);
      try {
        const res = await shopApi.getResturantById({
          id: Number(restaurantId),
          diningMode: selectedMode,
          adresse: { latitude: authLocation?.lat, longitude: authLocation?.lng },
        });
        const tokenRes = await catalogueApi.getEstablishementToken(Number(restaurantId));
        const newToken = tokenRes.data.data.result;
        localStorage.setItem("establishementToken", newToken);
        setEstablishementToken(newToken);
        setRestaurant(res?.data?.restaurant);
        setCurrentShifts(findCurrentShift(res?.data?.restaurant));
      } catch (e) {
        console.error("Error fetching data:", e);
      } finally {
        setLoadingState(false);
      }
    };
    fetchRestaurant();
  }, [restaurantId, menuMode, authLocation]);

  useEffect(() => {
    if (restaurant?.id && establishementToken) {
      dispatch(getCategoriesRequest(restaurant.id || 0));
    }
  }, [restaurant, establishementToken]);

  useEffect(() => {
    if (!restaurant || !userInfo?.qrCodeValue) return;
    const fetchClientData = async () => {
      try {
        const res = await loyalityApi.getClientBySecretCode(
          userInfo.qrCodeValue,
          restaurant.id
        );
        setUserLoyalityPoints(res?.data?.loyalityPoints);
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchClientData();
  }, [restaurant]);

  const checkRestaurantDelivery = (
    address: google.maps.places.PlaceResult | undefined
  ) => {
    deliveryApi
      .checkRestaurantDelivery(String(restaurantId), address)
      .then(() => {
        dispatch(updateAppState({ selectedMode: MODE_DELIVERY }));
        dispatch(setDeliveryAddress(address));
        setShowModal(false);
        setShowRestaurantError(false);
      })
      .catch(() => {
        setShowRestaurantError(true);
        setShowModal(false);
      });
  };

  const hideModal = () => {
    setShowModal(false);
    if (selectedMode === MODE_DELIVERY) {
      const mode = getRestaurantDefaultMode(restaurant!);
      if (mode) dispatch(updateAppState({ selectedMode: mode }));
    }
  };

  return (
    <div className="restaurant-details">
      <div className="background-overlay" />
      <AppNavbar setRestorentFilter={setRestorentFilter} RestorentFilter={RestorentFilter} />

      <Grid className="restaurant-grid">
        <Grid className="listing-menus">
          <PageHeader
            title={restaurant?.nom}
            subtitle={
              loadingState ? (
                "Loading..."
              ) : (
                <>
                  <ShiftInfo currentShifts={currentShifts} />
                  {restaurant?.distance && ` • ${restaurant.distance}Km`}
                  {restaurant && restaurant.tempsPrep !== 0 && ` • ${restaurant.tempsPrep} min`}
                  {restaurant?.specialites && ` • ${restaurant.specialites}`}
                </>
              )
            }
            backAction={() => {
              dispatch(clearStoreItem("menu"));
              navigateBasedOnRestaurantCount(restaurantCount, isGlobalWebsite, navigate);
            }}
          />

          <CategoryTabs
            categories={filteredCategories}
            activeTabIndex={activeTabIndex}
            onTabClick={scrollToSection}
            containerRef={tabsContainerRef}
          />

          <OrderTypeTabs
            restaurant={restaurant}
            selectedMode={selectedMode}
            onSelectMode={(type) => dispatch(updateAppState({ selectedMode: type }))}
            onSelectDelivery={() => setShowModal(true)}
          />

          <MenuSections
            categories={filteredCategories}
            restaurant={restaurant}
            userLoyalityPoints={userLoyalityPoints}
            sectionRefs={sectionRefs}
          />
        </Grid>
      </Grid>

      <AddressSelectionModal
        showModal={showModal}
        restaurantId={String(restaurantId)}
        hideModal={hideModal}
        onConfirmAddress={checkRestaurantDelivery}
      />
      <RestaurantErrorModal
        showRestaurantError={showRestaurantError}
        onPressBtn={() => {
          setShowRestaurantError(false);
          setShowModal(true);
        }}
        onCloseModal={() => setShowRestaurantError(false)}
        modalTitle={languageData.fr.delivery.deliveryErrorModal.title}
        modalDescription={languageData.fr.delivery.deliveryErrorModal.message}
        modalButtonLabel={languageData.fr.delivery.deliveryErrorModal.actionButtonLabel}
      />
    </div>
  );
};

export default withNavigateHook(RestaurantMenuPage);